// ============================================================================
// Moves — the Date / Estimate / Job-type cluster (2026-09-18).
// `BBM Context for claude/moves-architecture.md` is the design; this file is
// the shared vocabulary every surface uses so the rules live in one place.
//
//   clients ──< moves ──< estimates (options, lettered)
//                  └───< move_jobs (dispatch rows / legs)
//
// A move owns the job's identity: type, addresses, access, the three dates
// (pack · load · unload, each tentative on its own), the walkthrough, the
// confirmation log and the storage mode. Estimates are OPTIONS on a move
// (pricing only). The old date/address columns on clients + estimates are
// kept in sync by Postgres triggers (sql/094) for the transition — the app
// never writes them any more (see MOVE_MIRROR_* below), it writes the move.
// ============================================================================

import { kindId } from './phaseKinds';

// Planner phase shape (mirrors database.makePhase — not imported to avoid a
// database ↔ moves import cycle; normalizePhases re-validates on save).
const makePhase = ({ kind = 'move', sequence = 1, date = null, time = null, duration_days = 1, crew_size = 0, trucks_needed = '', dispatcher_notes = '' } = {}) => ({
  kind, sequence, date: date || null, time: time || null,
  duration_days: Math.max(1, Number(duration_days) || 1),
  crew_size: Number(crew_size) || 0, trucks_needed: trucks_needed || '', dispatcher_notes: dispatcher_notes || '',
  move_job_ids: [], move_job_id: null,
});

// Columns the mirror triggers copy move → estimates / clients. Saves of an
// estimate or client STRIP these (db.upsertEstimate / db.upsertClient): a
// stale full-row write must never drag the move backwards. New-client
// intake is the one exception — it seeds the lead move THROUGH the client
// insert (clients_ensure_move_trg reads them), so the insert keeps them.
export const MOVE_MIRROR_ESTIMATE = [
  'origin_address', 'destination_address', 'additional_stops', 'stop_details',
  'move_date', 'move_time', 'pack_date', 'pack_time', 'walkthrough_date', 'job_type',
  'packing_needed', 'heavy_items', 'description',
  'from_sqft', 'from_levels', 'from_stairs', 'from_elevator', 'from_driveway', 'from_walk_distance',
  'to_sqft', 'to_levels', 'to_stairs', 'to_elevator', 'to_driveway', 'to_walk_distance',
];
export const MOVE_MIRROR_CLIENT = [
  ...MOVE_MIRROR_ESTIMATE, 'date_tentative', 'walkthrough_status', 'access_notes',
  'from_apartment', 'from_apartment_floor', 'from_stairs_steps',
  'to_apartment', 'to_apartment_floor', 'to_stairs_steps',
];

// The move's own copy of those fields (same names except the dates).
export const MOVE_IDENTITY_FIELDS = [
  'job_type', 'origin_address', 'destination_address', 'additional_stops', 'stop_details',
  'packing_needed', 'heavy_items', 'description', 'access_notes',
  'from_sqft', 'from_levels', 'from_stairs', 'from_elevator', 'from_driveway', 'from_walk_distance',
  'from_apartment', 'from_apartment_floor', 'from_stairs_steps',
  'to_sqft', 'to_levels', 'to_stairs', 'to_elevator', 'to_driveway', 'to_walk_distance',
  'to_apartment', 'to_apartment_floor', 'to_stairs_steps',
];
export const MOVE_DATE_FIELDS = [
  'load_date', 'load_time', 'load_tentative',
  'unload_date', 'unload_window_end', 'unload_time', 'unload_tentative',
  'pack_date', 'pack_time', 'pack_tentative',
];

// Estimate row ← move (what the mirror would write). Used when the app
// creates an option itself so the row is complete before the trigger's
// first pass, and to keep the in-memory store consistent after saveMove.
export function estimateFieldsFromMove(m) {
  if (!m) return {};
  return {
    origin_address: m.origin_address ?? '', destination_address: m.destination_address ?? '',
    additional_stops: m.additional_stops ?? '', stop_details: m.stop_details ?? null,
    move_date: m.load_date ?? null, move_time: m.load_time ?? '', pack_date: m.pack_date ?? null, pack_time: m.pack_time ?? '',
    walkthrough_date: m.walkthrough_date ?? null, job_type: m.job_type ?? null,
    packing_needed: m.packing_needed ?? null, heavy_items: m.heavy_items ?? '', description: m.description ?? '',
    from_sqft: m.from_sqft ?? null, from_levels: m.from_levels ?? null, from_stairs: m.from_stairs ?? null, from_elevator: m.from_elevator ?? null, from_driveway: m.from_driveway ?? null, from_walk_distance: m.from_walk_distance ?? null,
    to_sqft: m.to_sqft ?? null, to_levels: m.to_levels ?? null, to_stairs: m.to_stairs ?? null, to_elevator: m.to_elevator ?? null, to_driveway: m.to_driveway ?? null, to_walk_distance: m.to_walk_distance ?? null,
  };
}
export function clientFieldsFromMove(m) {
  if (!m) return {};
  return {
    ...estimateFieldsFromMove(m),
    date_tentative: !!m.load_tentative, walkthrough_status: m.walkthrough_status ?? null, access_notes: m.access_notes ?? null,
    from_apartment: m.from_apartment ?? null, from_apartment_floor: m.from_apartment_floor ?? null, from_stairs_steps: m.from_stairs_steps ?? null,
    to_apartment: m.to_apartment ?? null, to_apartment_floor: m.to_apartment_floor ?? null, to_stairs_steps: m.to_stairs_steps ?? null,
  };
}
// Move ← a client/estimate-shaped object (intake drafts, old rows).
export function moveFieldsFromClientShape(c) {
  if (!c) return {};
  const out = {};
  MOVE_IDENTITY_FIELDS.forEach(k => { if (c[k] !== undefined) out[k] = c[k]; });
  if (c.move_date !== undefined) out.load_date = c.move_date || null;
  if (c.move_time !== undefined) out.load_time = c.move_time || null;
  if (c.date_tentative !== undefined) out.load_tentative = !!c.date_tentative;
  if (c.pack_date !== undefined) out.pack_date = c.pack_date || null;
  if (c.pack_time !== undefined) out.pack_time = c.pack_time || null;
  if (c.walkthrough_date !== undefined) out.walkthrough_date = c.walkthrough_date || null;
  if (c.walkthrough_status !== undefined) out.walkthrough_status = c.walkthrough_status || null;
  return out;
}

export const stripKeys = (obj, keys) => { const out = { ...obj }; keys.forEach(k => { delete out[k]; }); return out; };

// ── Lookups ────────────────────────────────────────────────────────────────
export const movesForClient = (moves, clientId) => (moves || []).filter(m => m.client_id === clientId)
  .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
export const currentMoveFor = (client, moves) => {
  if (!client) return null;
  const mine = movesForClient(moves, client.id);
  return mine.find(m => m.id === client.current_move_id) || mine.find(m => !MOVE_TERMINAL.has(m.status)) || mine[0] || null;
};
export const moveForEstimate = (estimate, moves) => (moves || []).find(m => m.id === estimate?.move_id) || null;
export const optionsForMove = (estimates, moveId) => (estimates || []).filter(e => e.move_id === moveId)
  .sort((a, b) => String(a.option_letter || '').localeCompare(String(b.option_letter || '')) || String(a.created_at || '').localeCompare(String(b.created_at || '')));
export const legsForMove = (jobs, moveId) => (jobs || []).filter(j => j.move_id === moveId && j.type !== 'WALKTHROUGH');

export const MOVE_TERMINAL = new Set(['completed', 'cancelled']);
export const OPTION_LIVE = new Set(['building', 'ready', 'quoted', 'pending_approval', 'follow_up']);
export const OPTION_TUCKED = new Set(['declined', 'cancelled']);

// ── Display ────────────────────────────────────────────────────────────────
export const MOVE_STATUS = {
  lead:        { label: 'Lead',        color: '#3b82f6', bg: '#eff6ff' },
  walkthrough: { label: 'Walkthrough', color: '#7c3aed', bg: '#f5f3ff' },
  quoting:     { label: 'Quoting',     color: '#8b5cf6', bg: '#f5f3ff' },
  quoted:      { label: 'Quoted',      color: '#eab308', bg: '#fefce8' },
  postponed:   { label: 'Postponed',   color: '#0369a1', bg: '#e0f2fe' },
  booked:      { label: 'Booked',      color: '#059669', bg: '#ecfdf5' },
  in_progress: { label: 'In progress', color: '#0891b2', bg: '#ecfeff' },
  completed:   { label: 'Completed',   color: '#166534', bg: '#f0fdf4' },
  cancelled:   { label: 'Cancelled',   color: '#ef4444', bg: '#fef2f2' },
};
export const moveStatusMeta = (s) => MOVE_STATUS[s] || MOVE_STATUS.lead;

export const JOB_TYPE_SHORT = { LOCAL: 'Local', LONG_DISTANCE: 'Out-of-State', DELIVERY: 'Delivery', DUMP_RUN: 'Dump run', LABOR_ONLY: 'Labor only', PICKUP: 'Pickup', PACKING: 'Packing' };
export const jobTypeShort = (t) => JOB_TYPE_SHORT[t] || (t ? String(t).replace(/_/g, ' ') : 'Move');

const MS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function fmtShortDate(s) {
  if (!s) return '';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/); if (!m) return String(s);
  return `${MS[Number(m[2]) - 1]} ${Number(m[3])}`;
}
export function fmtShortDateYear(s) {
  if (!s) return '';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/); if (!m) return String(s);
  return `${MS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}
export function daysBetween(a, b) {
  const pa = parseYmdLocal(a), pb = parseYmdLocal(b);
  if (!pa || !pb) return null;
  return Math.round((pb - pa) / 86400000);
}
export function parseYmdLocal(s) {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}
export function addDaysYmd(s, n) {
  const d = parseYmdLocal(s); if (!d) return null;
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// "~Sep 20" for tentative, "Sep 20–22" for a window.
export function fmtMoveDate(date, { tentative = false, windowEnd = null, year = false } = {}) {
  if (!date) return '';
  const f = year ? fmtShortDateYear : fmtShortDate;
  let s = f(date);
  if (windowEnd && windowEnd !== date) {
    const sameMonth = String(windowEnd).slice(0, 7) === String(date).slice(0, 7);
    s = sameMonth ? `${fmtShortDate(date)}–${Number(String(windowEnd).slice(8, 10))}` : `${fmtShortDate(date)}–${fmtShortDate(windowEnd)}`;
    if (year) s += `, ${String(date).slice(0, 4)}`;
  }
  return tentative ? `~${s}` : s;
}

// The chip label in the move switcher: "Load Sep 16 · Out-of-State".
// What the load date is CALLED (Jaylen 2026-09-18, after the build): most
// jobs load and unload the same day, so the one date is the "Move day".
// It becomes "Load" only once the trip has a separate unload — an unload
// date, a long-distance type, or a storage mode. Small job types say "Job
// day". Used by the trip strip, chips, summaries and the Book chips so the
// word is the same everywhere.
export function loadDateLabel(m) {
  if (!m) return 'Move day';
  if (isSmallJobType(m.job_type)) return 'Job day';
  if (m.unload_date || isTripType(m.job_type) || m.storage_mode) return 'Load';
  return 'Move day';
}
// Short verb for chips: "Move Oct 9" / "Load Sep 16" / "Oct 9" (small types).
const loadVerb = (m) => { const l = loadDateLabel(m); return l === 'Load' ? 'Load ' : l === 'Move day' ? 'Move ' : ''; };

export function moveChipLabel(m) {
  if (!m) return 'Move';
  const t = jobTypeShort(m.job_type);
  if (!m.load_date) return `${t} · no date`;
  return `${loadVerb(m)}${fmtMoveDate(m.load_date, { tentative: m.load_tentative })} · ${t}`;
}
// "Pack Sep 14 · Load Sep 16 · Unload ~Sep 20"
export function moveDatesSummary(m) {
  if (!m) return '';
  const parts = [];
  if (m.pack_date) parts.push(`Pack ${fmtMoveDate(m.pack_date, { tentative: m.pack_tentative })}`);
  if (m.load_date) parts.push(`${loadVerb(m)}${fmtMoveDate(m.load_date, { tentative: m.load_tentative })}`);
  if (m.unload_date) parts.push(`Unload ${fmtMoveDate(m.unload_date, { tentative: m.unload_tentative, windowEnd: m.unload_window_end })}`);
  return parts.join(' · ') || 'No dates yet';
}
export function moveRoute(m) {
  if (!m) return '';
  const a = (m.origin_address || '').split(',')[0].trim();
  const b = (m.destination_address || '').split(',')[0].trim();
  if (a && b) return `${a} → ${b}`;
  return a || b || '';
}
// Option label: "021-A" when the move has several options, else "021".
export function optionLabel(est, siblingsCount = 1) {
  const num = (est?.job_number || '').replace(/^BBM-\d{4}-/, '') || est?.job_number || '';
  return siblingsCount > 1 && est?.option_letter ? `${num}-${est.option_letter}` : num;
}

// ── Dates ──────────────────────────────────────────────────────────────────
export const dateKeysOf = (m) => {
  const keys = [];
  if (m?.pack_date) keys.push({ key: 'pack', label: 'Pack day', date: m.pack_date, time: m.pack_time, tentative: !!m.pack_tentative });
  if (m?.load_date) keys.push({ key: 'load', label: loadDateLabel(m), date: m.load_date, time: m.load_time, tentative: !!m.load_tentative });
  if (m?.unload_date) keys.push({ key: 'unload', label: 'Unload', date: m.unload_date, windowEnd: m.unload_window_end, time: m.unload_time, tentative: !!m.unload_tentative });
  return keys;
};
export const anyTentative = (m) => dateKeysOf(m).some(k => k.tentative);
export const tentativeKeys = (m) => dateKeysOf(m).filter(k => k.tentative).map(k => k.key);

// Is a job type one where the truck goes somewhere else to unload?
export const isTripType = (t) => t === 'LONG_DISTANCE';
export const isSmallJobType = (t) => ['DELIVERY', 'PICKUP', 'DUMP_RUN', 'LABOR_ONLY'].includes(t);

// Dates-to-confirm (rule 6): booked moves inside N days with a tentative date.
export function datesToConfirm(moves, { today = todayYmd(), days = 7 } = {}) {
  const limit = addDaysYmd(today, days);
  return (moves || []).filter(m => (m.status === 'booked' || m.status === 'in_progress') && anyTentative(m))
    .map(m => ({ move: m, soonest: dateKeysOf(m).filter(k => k.tentative).map(k => k.date).sort()[0] }))
    .filter(x => x.soonest && x.soonest <= limit)
    .sort((a, b) => a.soonest.localeCompare(b.soonest));
}
export function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Planner pre-fill (rule 8) ─────────────────────────────────────────────
// PACK (if pack date) → LOAD → DRIVE (driveDays) → HOLD (gap) → UNLOAD → RETURN.
// Local / small jobs: one MOVE (or the small-job kind) day. Storage-included:
// LOAD → HOLD(storage) → UNLOAD. Returns planner phases (makePhase shape),
// dates set from the move; the operator edits from there.
export function deriveTripPhases(m, { driveDays = 1, crewSize = 0, trucks = '' } = {}) {
  if (!m) return [];
  const mk = (kind, extra = {}) => makePhase({ kind, crew_size: crewSize, trucks_needed: trucks, ...extra });
  const out = [];
  if (isSmallJobType(m.job_type)) {
    const kind = m.job_type === 'DUMP_RUN' ? 'trash_removal' : (m.job_type === 'DELIVERY' ? 'delivery' : m.job_type === 'PICKUP' ? 'pickup' : 'move');
    out.push(mk(kind, { date: m.load_date || null, time: m.load_time || null }));
    return out.map((p, i) => ({ ...p, sequence: i + 1 }));
  }
  if (m.pack_date) out.push(mk('pack', { date: m.pack_date, time: m.pack_time || null }));
  const trip = isTripType(m.job_type) || (m.unload_date && m.load_date && daysBetween(m.load_date, m.unload_date) > 0);
  if (!trip) {
    out.push(mk('move', { date: m.load_date || null, time: m.load_time || null }));
    return out.map((p, i) => ({ ...p, sequence: i + 1 }));
  }
  out.push(mk('load', { date: m.load_date || null, time: m.load_time || null }));
  const gap = m.load_date && m.unload_date ? daysBetween(m.load_date, m.unload_date) : null; // days between load and unload
  if (m.storage_mode === 'included') {
    out.push(mk('hold', { duration_days: gap && gap > 1 ? gap - 1 : 1 }));
    out.push(mk('unload', { date: m.unload_date || null, time: m.unload_time || null }));
    out.push(mk('return'));
    return out.map((p, i) => ({ ...p, sequence: i + 1 }));
  }
  const drive = Math.max(1, driveDays);
  const holdDays = gap == null ? 0 : Math.max(0, gap - drive - 1); // days the loaded truck waits before the drive
  if (holdDays > 0) out.push(mk('hold', { duration_days: holdDays }));
  if (drive >= 1 && gap != null && gap - holdDays - 1 >= 1) out.push(mk('drive', { duration_days: drive }));
  const unloadSame = gap != null && gap - holdDays <= 1; // unload on the drive's arrival day
  if (unloadSame) {
    // collapse drive + unload into one day when the truck arrives and empties the same day
    const last = out[out.length - 1];
    if (last && last.kind === 'drive') { last.kind = kindId(['drive', 'unload']); last.date = m.unload_date || null; }
    else out.push(mk('unload', { date: m.unload_date || null, time: m.unload_time || null }));
  } else {
    out.push(mk('unload', { date: m.unload_date || null, time: m.unload_time || null }));
  }
  out.push(mk('return', { duration_days: drive }));
  return out.map((p, i) => ({ ...p, sequence: i + 1 }));
}
